// Consistent dataLayer schema + Google Consent Mode v2 helpers.
//
// Every event pushed via `trackEvent` includes a stable envelope:
//   { event, action, page_type, page_path, game_id?, jackpot_id?, ...extra }
//
// Use `setPageContext` on route change to update the page_type/game/jackpot
// context so subsequent events inherit those params without repeating them.

export type ConsentState = "granted" | "denied";

export type PageType =
  | "home"
  | "game"
  | "game_history"
  | "game_results"
  | "how_to_play"
  | "jackpot_analysis"
  | "state"
  | "state_game"
  | "instant_game"
  | "results"
  | "results_date"
  | "tools"
  | "tool"
  | "my_picks"
  | "admin"
  | "other";

export interface PageContext {
  page_type: PageType;
  page_path?: string;
  game_id?: string;
  jackpot_id?: string;
  state_id?: string;
}

export interface TrackEventParams {
  action: string;
  page_type?: PageType;
  game_id?: string;
  jackpot_id?: string;
  state_id?: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    __ulcPageCtx?: PageContext;
  }
}

const CONSENT_KEY = "ulc_consent_v1";

export interface StoredConsent {
  granted: boolean;
  timestamp: number;
  version: 1;
}

export function readStoredConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed && typeof parsed.granted === "boolean") return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

export function writeStoredConsent(granted: boolean): void {
  if (typeof window === "undefined") return;
  try {
    const payload: StoredConsent = { granted, timestamp: Date.now(), version: 1 };
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Push a Google Consent Mode v2 `consent update` to the dataLayer. */
export function updateConsent(granted: boolean): void {
  if (typeof window === "undefined") return;
  const state: ConsentState = granted ? "granted" : "denied";
  window.dataLayer = window.dataLayer ?? [];
  // Use the `gtag` shim installed in __root.tsx if available; otherwise push directly.
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      analytics_storage: state,
    });
  } else {
    window.dataLayer.push({
      event: "consent_update",
      "gtm.consent": {
        ad_storage: state,
        ad_user_data: state,
        ad_personalization: state,
        analytics_storage: state,
      },
    });
  }
  writeStoredConsent(granted);
  window.dataLayer.push({ event: "consent_change", action: "consent_change", granted });
}

/** Set the ambient page context. Subsequent trackEvent calls inherit it. */
export function setPageContext(ctx: PageContext): void {
  if (typeof window === "undefined") return;
  window.__ulcPageCtx = ctx;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: "page_view",
    action: "page_view",
    page_type: ctx.page_type,
    page_path: ctx.page_path,
    game_id: ctx.game_id,
    jackpot_id: ctx.jackpot_id,
    state_id: ctx.state_id,
  });
}

/** Push an event to the dataLayer, merged with the current page context. */
export function trackEvent(event: string, params: TrackEventParams): void {
  if (typeof window === "undefined") return;
  const ctx = window.__ulcPageCtx ?? { page_type: "other" as PageType };
  window.dataLayer = window.dataLayer ?? [];
  const { action, page_type, game_id, jackpot_id, state_id, ...extra } = params;
  window.dataLayer.push({
    event,
    action,
    page_type: page_type ?? ctx.page_type,
    page_path: ctx.page_path,
    game_id: game_id ?? ctx.game_id,
    jackpot_id: jackpot_id ?? ctx.jackpot_id,
    state_id: state_id ?? ctx.state_id,
    ...extra,
  });
}

/** Infer a PageType from a pathname when a route doesn't set one explicitly. */
export function inferPageType(pathname: string): PageType {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/games/") && pathname.includes("/history")) return "game_history";
  if (pathname.startsWith("/games/") && pathname.includes("/results/")) return "game_results";
  if (pathname.startsWith("/games/")) return "game";
  if (pathname.endsWith("/how-to-play")) return "how_to_play";
  if (pathname.endsWith("/jackpot-analysis")) return "jackpot_analysis";
  if (pathname.startsWith("/states/") && pathname.includes("/instant/")) return "instant_game";
  if (pathname.match(/^\/states\/[^/]+\/[^/]+/)) return "state_game";
  if (pathname.startsWith("/states")) return "state";
  if (pathname.startsWith("/results/")) return "results_date";
  if (pathname.startsWith("/results")) return "results";
  if (pathname === "/tools" || pathname === "/tools/") return "tools";
  if (pathname.startsWith("/tools/")) return "tool";
  if (pathname.startsWith("/my-picks")) return "my_picks";
  if (pathname.startsWith("/admin")) return "admin";
  return "other";
}